import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const pillars = [
  {
    number: '01',
    title: 'Solutions',
    text: 'Custom platforms, connected workflows, and AI automation designed around the way your organization actually works.',
    link: '/services',
    label: 'Explore solutions',
  },
  {
    number: '02',
    title: 'Products',
    text: 'Focused software for education and hospitality, built to replace fragmented tools with one operational system.',
    link: '/products',
    label: 'View products',
  },
  {
    number: '03',
    title: 'Academy',
    text: 'Practical learning paths for institutions, teams, and early-career technologists who want to build real systems.',
    link: '/academy',
    label: 'Visit the Academy',
  },
];

const solutions = [
  ['Custom Software', 'Operational platforms shaped around your processes, roles, and growth plan.'],
  ['AI Automation', 'Human-reviewed automations that reduce repetitive work and connect existing tools.'],
  ['Education Technology', 'Learning and institution systems for students, faculty, administration, and reporting.'],
  ['Systems Integration', 'Secure connections between the software your teams already depend on.'],
  ['Digital Products', 'Product strategy, experience design, engineering, and launch support in one delivery team.'],
  ['Modernization', 'A practical path from brittle legacy workflows to maintainable, observable software.'],
];

const products = [
  {
    name: 'Path View',
    category: 'Education operations',
    description: 'A connected environment for learning delivery, administration, communication, and institutional visibility.',
    link: '/products/path-view',
    accent: 'PV',
  },
  {
    name: 'Restomatic',
    category: 'Restaurant operations',
    description: 'A restaurant ecosystem that brings ordering, service, kitchen workflows, and management into one experience.',
    link: '/products/restomatic',
    accent: 'RM',
  },
];

export default function SynchronizedHome() {
  useEffect(() => {
    document.title = 'Nalexus Technologies | Custom Software, AI Automation & EdTech';
  }, []);

  return (
    <main id="main-content" className="sync-main">
      <section className="sync-hero sync-section">
        <div className="sync-shell sync-hero-grid">
          <div className="sync-hero-copy">
            <p className="sync-eyebrow">Custom Software <span>•</span> AI Automation <span>•</span> Education Technology</p>
            <h1>Software that runs <em>businesses</em> and institutions.</h1>
            <p className="sync-lead">
              Nalexus designs connected software, automation, and digital products that turn complex operations into clear, dependable systems.
            </p>
            <div className="sync-actions">
              <Link className="sync-button" to="/services">Explore solutions</Link>
              <Link className="sync-text-link" to="/contact?intent=demo">Request a demo <span aria-hidden="true">↗</span></Link>
            </div>
          </div>

          <div className="sync-system-card" aria-label="Illustration of a connected Nalexus software system">
            <div className="sync-system-topline">
              <span>Nalexus system</span>
              <span className="sync-status"><i aria-hidden="true" /> Connected</span>
            </div>
            <div className="sync-system-core">
              <div className="sync-core-mark">N</div>
              <p>One operational layer</p>
              <span>Software · Data · Automation</span>
            </div>
            <div className="sync-system-nodes">
              <span>People</span>
              <span>Workflows</span>
              <span>Insights</span>
            </div>
          </div>
        </div>
      </section>

      <section className="sync-section sync-pillar-section" aria-labelledby="operating-model-heading">
        <div className="sync-shell">
          <div className="sync-section-heading">
            <p className="sync-kicker">One technology partner</p>
            <h2 id="operating-model-heading">Three ways to move your organization forward.</h2>
          </div>
          <div className="sync-pillar-grid">
            {pillars.map((pillar) => (
              <article key={pillar.title} className="sync-card sync-pillar-card">
                <span className="sync-card-number">{pillar.number}</span>
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
                <Link to={pillar.link}>{pillar.label} <span aria-hidden="true">→</span></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sync-section sync-solutions-section" aria-labelledby="solutions-heading">
        <div className="sync-shell">
          <div className="sync-section-heading sync-heading-row">
            <div>
              <p className="sync-kicker">Solutions</p>
              <h2 id="solutions-heading">Built around the operation, not a template.</h2>
            </div>
            <p>We start with the decision, workflow, or service that needs to improve. Technology follows the operating need.</p>
          </div>
          <div className="sync-solution-grid">
            {solutions.map(([title, text], index) => (
              <article key={title} className="sync-solution-item">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
          <Link className="sync-inline-cta" to="/contact">Discuss your system <span aria-hidden="true">→</span></Link>
        </div>
      </section>

      <section className="sync-section sync-products-section" aria-labelledby="products-heading">
        <div className="sync-shell">
          <div className="sync-section-heading">
            <p className="sync-kicker">Products</p>
            <h2 id="products-heading">Focused platforms for demanding operations.</h2>
          </div>
          <div className="sync-product-grid">
            {products.map((product) => (
              <article key={product.name} className="sync-product-card">
                <div className="sync-product-visual" aria-hidden="true">
                  <span>{product.accent}</span>
                  <div className="sync-product-lines"><i /><i /><i /></div>
                </div>
                <div className="sync-product-copy">
                  <p>{product.category}</p>
                  <h3>{product.name}</h3>
                  <span>{product.description}</span>
                  <Link to={product.link}>See product <b aria-hidden="true">↗</b></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sync-section sync-method-section" aria-labelledby="method-heading">
        <div className="sync-shell sync-method-grid">
          <div className="sync-section-heading">
            <p className="sync-kicker">How we work</p>
            <h2 id="method-heading">Clarity before code. Evidence before scale.</h2>
          </div>
          <ol className="sync-method-list">
            <li><span>01</span><div><h3>Understand the operation</h3><p>Map users, decisions, constraints, and the outcomes the system needs to support.</p></div></li>
            <li><span>02</span><div><h3>Design the smallest useful release</h3><p>Make workflows tangible early, validate them with the people who will use them, then refine.</p></div></li>
            <li><span>03</span><div><h3>Build for dependable change</h3><p>Ship maintainable software with clear ownership, measured performance, and a practical roadmap.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="sync-section sync-final-cta" aria-labelledby="final-cta-heading">
        <div className="sync-shell sync-final-cta-inner">
          <p className="sync-kicker">A better system starts with one useful conversation.</p>
          <h2 id="final-cta-heading">What should your organization be able to do next?</h2>
          <div className="sync-actions">
            <Link className="sync-button" to="/contact">Start a project</Link>
            <Link className="sync-text-link" to="/products">Explore products <span aria-hidden="true">→</span></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
